import { faInfoCircle, faQuestionCircle, faTag } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";

import { PriceSummaryItemKind, PriceSummary as PriceSummaryType } from "common/types/priceSummary";
import CommonPriceSummary, {
  PriceSummaryPromotionLinkProps as CommonPriceSummaryLinkProps,
  PriceSummaryProps as CommonPriceSummaryProps,
  RenderItemLabel,
} from "common/components/PriceSummary";
import Icon from "common/components/Icon";
import { Optional } from "common/utils/types";

import { usePromotionDrawer } from "client/products/stackables";
import Tooltip from "client/common/components/content/Tooltip";
import { SERVICE_FEE_HELP_TEXT } from "client/orders/constants";

import TextButton from "../buttons/TextButton";

import commonPriceSummaryStyles from "common/styles/components/PriceSummary.module.less";
import styles from "../../styles/PriceSummary.module.less";

type PriceSummaryPromotionLinkProps = CommonPriceSummaryLinkProps & {
  onNavigate?: () => void;
};

const DefaultPriceSummaryPromotionLink = ({
  label,
  incentive,
  rebate,
  is_promo_code,
  onNavigate,
}: PriceSummaryPromotionLinkProps) => {
  const { open: openPromotionDrawer } = usePromotionDrawer();
  const promotion = incentive || rebate;

  return (
    <TextButton
      onClick={() => openPromotionDrawer({ kind: promotion.kind, id: promotion.id, onNavigate })}
      icon={faInfoCircle}
      className={styles.promotionLink}
    >
      {is_promo_code && <Icon className={commonPriceSummaryStyles.promoIcon} icon={faTag} />}
      {label}
    </TextButton>
  );
};

const defaultRenderItemLabel: RenderItemLabel = ({ kind, label, className }) => {
  if (kind === PriceSummaryItemKind.ClientPurchasingFee) {
    return (
      <Tooltip
        content={<div className={styles.serviceFeeTooltipContent}>{SERVICE_FEE_HELP_TEXT}</div>}
        isEnabled={true}
      >
        <span className={cx(className)}>
          {label}&nbsp;
          <Icon icon={faQuestionCircle} className={styles.serviceFeeTooltipIcon} />
        </span>
      </Tooltip>
    );
  }
  return null;
};

type PriceSummaryProps<T extends PriceSummaryType> = CommonPriceSummaryProps<T> & {
  onNavigate?: () => void;
};

const PriceSummary = <T extends PriceSummaryType>({
  styles: commonStyles,
  onNavigate,
  renderPromotionLink = (p) => <DefaultPriceSummaryPromotionLink onNavigate={onNavigate} {...p} />,
  renderItemLabel = defaultRenderItemLabel,
  data,
}: PriceSummaryProps<T>) => {
  return (
    <CommonPriceSummary
      data={data}
      styles={commonStyles}
      renderPromotionLink={renderPromotionLink}
      renderItemLabel={renderItemLabel}
    />
  );
};

type PriceSummaryExternalTaxProps<T extends PriceSummaryType> = PriceSummaryProps<T> & {
  tax: Optional<number>;
  isTaxFinal: boolean;
};

/** For cases where the tax is sourced from somewhere outside data */
const PriceSummaryExternalTax = <T extends PriceSummaryType>({
  data,
  styles: commonStyles,
  tax,
  isTaxFinal,
  ...rest
}: PriceSummaryExternalTaxProps<T>) => {
  const total = tax ? data.total + tax : data.total;
  return (
    <PriceSummary
      {...rest}
      data={{ ...data, tax, total, is_tax_final: isTaxFinal }}
      styles={commonStyles}
    />
  );
};

export default Object.assign(PriceSummary, { ExternalTax: PriceSummaryExternalTax });
